/**
 * Phase 9.7.21 — 🎁 Virtual Gifts explainer page
 * Route: /virtual-gifts
 *
 * Marketing page that explains the gift catalog + how fans send +
 * how artists receive. Pulls the live gift catalog from
 * GET /api/gifts/catalog so the page is always in sync with the
 * actual virtual goods on offer.
 */
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import SharedNavigation from "@/components/SharedNavigation";
import useCurrentUser from "@/lib/useCurrentUser";

const fmt = (n) => Number(n || 0).toLocaleString();

const TIER_META = {
  starter:  { label: "Starter",  cls: "border-emerald-400/40 bg-emerald-500/10 text-emerald-200" },
  hype:     { label: "Hype",     cls: "border-sky-400/40 bg-sky-500/10 text-sky-200" },
  premium:  { label: "Premium",  cls: "border-amber-400/40 bg-amber-500/10 text-amber-200" },
  legendary:{ label: "Legendary",cls: "border-fuchsia-400/40 bg-fuchsia-500/10 text-fuchsia-200" },
  mythic:   { label: "Mythic",   cls: "border-purple-400/50 bg-purple-500/15 text-purple-200" },
};

// Phase 9.7.21 — Bridge the marketing tier vocabulary (Starter/Hype/
// Premium/Legendary/Mythic) onto the existing backend gift catalog
// tier names (tiny/small/medium/large/legendary) without renaming the
// catalog (the original names ship to admin gift CRUD, room overlays,
// wallets, and top-gifters — a rename touches all of those).
// Mapping preserves the price ordering low→high.
const BACKEND_TO_DISPLAY_TIER = {
  tiny: "starter",
  small: "hype",
  medium: "premium",
  large: "legendary",
  legendary: "mythic",
};
const giftDisplayTier = (g) =>
  BACKEND_TO_DISPLAY_TIER[g.tier] || g.tier || "starter";

const STEPS = [
  { icon: "🎧", title: "Discover a track you love",
    desc: "Open any track page — Explore, Featured Push, Live Beams, anywhere on RGM Studio." },
  { icon: "🪙", title: "Buy RGM Coins",
    desc: "Top up coins in your wallet — Stripe checkout, Cash App, Apple Pay supported." },
  { icon: "🎁", title: "Send a gift in one tap",
    desc: "Pick a gift from the catalog. An animated overlay plays in the room or on the track." },
  { icon: "💸", title: "Artists withdraw earnings",
    desc: "Artists track gift coins in real time. Withdraw via PayPal, Cash App, Zelle, Bank, or USDT." },
];

const VirtualGiftsPage = () => {
  const [gifts, setGifts] = useState([]);
  const [loading, setLoading] = useState(true);
  const { currentUser } = useCurrentUser();

  useEffect(() => {
    (async () => {
      try {
        const r = await api.get("/gifts/catalog");
        setGifts(r.data?.gifts || []);
      } catch {
        setGifts([]);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const tiers = ["starter", "hype", "premium", "legendary", "mythic"];
  const tieredGifts = tiers
    .map((t) => ({
      tier: t,
      items: gifts.filter((g) => giftDisplayTier(g) === t),
    }))
    .filter((b) => b.items.length > 0);

  return (
    <div className="min-h-screen bg-black text-white">
      <SharedNavigation currentUser={currentUser} />

      {/* Hero */}
      <header className="max-w-5xl mx-auto px-4 sm:px-6 pt-24 sm:pt-28 pb-10 text-center">
        <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-pink-500/20 border border-pink-400/40 text-pink-200 text-[10px] uppercase tracking-[3px] font-black">
          🎁 Virtual Gifts
        </div>
        <h1 className="mt-4 text-4xl sm:text-6xl font-black bg-gradient-to-r from-amber-300 via-pink-300 to-purple-300 bg-clip-text text-transparent">
          Real Support
          <br />
          Real Earnings.
        </h1>
        <p className="mt-4 text-amber-100/80 max-w-2xl mx-auto text-sm sm:text-base">
          Fans send virtual gifts to support artists they love. Artists keep
          a transparent share of every coin. Gifts power animated room
          overlays, leaderboard placements, and the Most Pushed rankings.
        </p>
        <div className="mt-6 flex justify-center gap-3 flex-wrap">
          <Link
            to="/wallet"
            className="px-5 py-3 rounded-full bg-gradient-to-r from-amber-500 to-pink-500 hover:from-amber-400 hover:to-pink-400 text-white font-bold shadow-lg shadow-amber-500/30"
            data-testid="gifts-cta-buy-coins"
          >
            🪙 Buy RGM Coins
          </Link>
          <Link
            to="/explore"
            className="px-5 py-3 rounded-full bg-white/5 border border-amber-400/40 text-amber-100 hover:bg-white/10 font-bold"
            data-testid="gifts-cta-find-artist"
          >
            🎧 Find an artist
          </Link>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-4 sm:px-6 pb-20 space-y-12">
        {/* How it works */}
        <section data-testid="gifts-how-it-works">
          <h2 className="text-xl sm:text-2xl font-black text-white">How it works</h2>
          <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {STEPS.map((s, i) => (
              <div key={s.title} className="rounded-2xl border border-amber-400/30 bg-gradient-to-br from-amber-500/5 to-black p-5">
                <div className="text-3xl">{s.icon}</div>
                <p className="mt-2 text-[10px] uppercase tracking-widest text-amber-300/70">
                  Step {i + 1}
                </p>
                <p className="text-white font-bold text-base mt-1">{s.title}</p>
                <p className="mt-2 text-sm text-amber-100/70 leading-relaxed">{s.desc}</p>
              </div>
            ))}
          </div>
        </section>

        {/* Gift catalog */}
        <section data-testid="gifts-catalog">
          <h2 className="text-xl sm:text-2xl font-black text-white">The gift catalog</h2>
          {loading && <p className="text-sm text-amber-200/60 mt-3">Loading catalog…</p>}
          {!loading && tieredGifts.length === 0 && (
            <p className="text-sm text-amber-200/70 mt-3">No gifts available yet — check back soon.</p>
          )}
          <div className="mt-4 space-y-6">
            {tieredGifts.map(({ tier, items }) => {
              const meta = TIER_META[tier] || { label: tier, cls: "border-amber-400/30 bg-amber-500/10 text-amber-200" };
              return (
                <div key={tier} data-testid={`gifts-tier-${tier}`}>
                  <div className="flex items-center gap-2 mb-3">
                    <span className={`text-[10px] uppercase tracking-widest font-black px-2 py-1 rounded border ${meta.cls}`}>
                      {meta.label} Tier
                    </span>
                    <span className="text-xs text-amber-200/60">{items.length} gift{items.length === 1 ? "" : "s"}</span>
                  </div>
                  <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
                    {items.map((g) => (
                      <div
                        key={g.id}
                        className="p-4 rounded-xl border border-amber-400/20 bg-gradient-to-br from-amber-900/10 to-black hover:border-amber-400/50 transition-colors text-center"
                        data-testid={`gift-card-${g.id}`}
                      >
                        <div className="text-4xl">{g.emoji || "🎁"}</div>
                        <p className="mt-2 text-white font-bold text-sm truncate">{g.name}</p>
                        <p className="mt-1 text-[11px] text-amber-300 font-bold">
                          🪙 {fmt(g.coins ?? g.coin_cost ?? g.cost ?? g.price ?? 0)}
                        </p>
                      </div>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        </section>

        {/* Artist payout note */}
        <section className="rounded-2xl border border-emerald-400/30 bg-gradient-to-br from-emerald-500/10 to-black p-6" data-testid="gifts-artist-payout">
          <h2 className="text-xl font-black text-white">For artists</h2>
          <p className="mt-2 text-sm text-amber-100/80">
            Every coin gifted to you accrues in your wallet. Withdraw at any time
            via PayPal, Cash App, Zelle, Bank Transfer, or USDT crypto wallet.
            Earnings are reviewed by RGM Studio and paid out manually for the
            best fraud protection.
          </p>
          <div className="mt-4 flex flex-wrap gap-2">
            <Link
              to="/wallet"
              className="px-4 py-2 rounded-full bg-emerald-500/20 hover:bg-emerald-500/30 border border-emerald-400/40 text-emerald-200 text-xs font-bold"
              data-testid="gifts-cta-wallet"
            >
              💼 Open my wallet
            </Link>
            <Link
              to="/payouts"
              className="px-4 py-2 rounded-full bg-white/5 hover:bg-white/10 border border-amber-400/40 text-amber-100 text-xs font-bold"
              data-testid="gifts-cta-payouts"
            >
              💸 Withdraw earnings
            </Link>
          </div>
        </section>
      </main>
    </div>
  );
};

export default VirtualGiftsPage;
